import { ref, watch, type Ref } from 'vue';
import { campaignsClient } from '~/services';
import { useMessageRules } from '~/components/quick-blast/useMessageRules';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { arraysEqual } from '~/utils/array';
import { buildCampaignMessage } from '~/constants/campaign';

interface ValidationError {
    message: string;
    severity: 'blocking' | 'warning';
    ai_agent?: string;
}

interface FieldBinding {
    key: string;
    value: string | null;
}

interface UseValidateMessageStandardsOptions {
    campaignType: () => 'marketing' | 'transactional';
    messageBody: () => string;
    messageMediaUrl: () => string | null | undefined;
    messageMediaContentType: () => string | null | undefined;
    messageSnippetId: () => number | null | undefined;
    messageSnippetFieldBindings: () => FieldBinding[];
    snippetIsLocked: () => boolean;
    audienceSegmentIds: () => number[];
    audienceExternalContactListIds: () => number[];
    audienceGroupIds: () => string[];
    validationContext?: {
        orgUnitId: () => number | undefined;
        aiVettingEnabled: () => boolean;
    };
}

interface UseValidateMessageStandardsReturn {
    bodyErrors: Ref<ValidationError[]>;
    previousWarnings: Ref<ValidationError[]>;
    validate: () => Promise<boolean>;
    clearErrors: () => void;
}

export const useValidateMessageStandards = (
    options: UseValidateMessageStandardsOptions
): UseValidateMessageStandardsReturn => {
    const teamId = Spark.state.currentTeam.id;
    const { isAiVettingEnabled } = useMessageRules();
    const { currentOrgUnit, currentGroup } = useCurrentGroup();
    const toasted = useToasted();

    const bodyErrors = ref<ValidationError[]>([]);
    const previousWarnings = ref<ValidationError[]>([]);

    watch(
        [
            () => options.messageBody(),
            () => options.messageMediaUrl(),
            () => options.messageMediaContentType(),
            () => options.messageSnippetId(),
            () => JSON.stringify(options.messageSnippetFieldBindings()),
            () => options.snippetIsLocked(),
            () => options.campaignType(),
            () => JSON.stringify(options.audienceSegmentIds()),
            () => JSON.stringify(options.audienceExternalContactListIds()),
            () => JSON.stringify(options.audienceGroupIds()),
        ],
        () => {
            previousWarnings.value = [];
        }
    );

    watch([() => options.snippetIsLocked()], () => {
        clearErrors();
    });

    const requestValidateMessageStandards = async () => {
        const audience: Record<string, string | number[] | string[] | undefined> = {
            type: options.campaignType(),
            segment_ids: options.audienceSegmentIds(),
            external_contact_list_ids: options.audienceExternalContactListIds(),
        };

        let orgUnitId: number | undefined;
        let groupId: string | undefined;

        if (options.validationContext) {
            orgUnitId = options.validationContext.orgUnitId();
        } else {
            orgUnitId = currentOrgUnit.value?.id;
            groupId = currentGroup.value?.group_id;
        }

        if (orgUnitId) {
            audience.group_ids = options.audienceGroupIds();
        }

        const payload = {
            audience,
            campaign_message: buildCampaignMessage({
                snippetId: options.messageSnippetId(),
                snippetIsLocked: options.snippetIsLocked(),
                fieldBindings: options.messageSnippetFieldBindings() || [],
                body: options.messageBody(),
                mediaUrl: options.messageMediaUrl(),
                mediaContentType: options.messageMediaContentType(),
            }),
        };

        if (orgUnitId) {
            return campaignsClient.orgUnitValidateMessageStandards(teamId, orgUnitId, payload);
        } else if (groupId) {
            return campaignsClient.groupValidateMessageStandards(teamId, groupId, payload);
        }
    };

    const validate = async (): Promise<boolean> => {
        if (options.snippetIsLocked()) {
            clearErrors();
            return true;
        }

        const aiEnabled = options.validationContext
            ? options.validationContext.aiVettingEnabled()
            : isAiVettingEnabled(options.campaignType());

        if (!aiEnabled) {
            clearErrors();
            return true;
        }

        try {
            const response = await requestValidateMessageStandards();

            if (!response) {
                return false;
            }

            const errors: ValidationError[] = response.data.errors || [];

            if (errors.length === 0) {
                previousWarnings.value = [];
                clearErrors();
                return true;
            }

            const blockingErrors = errors.filter((e) => e.severity === 'blocking');
            const warningErrors = errors.filter((e) => e.severity === 'warning');

            if (blockingErrors.length > 0) {
                bodyErrors.value = errors;
                previousWarnings.value = [];
                return false;
            }

            if (warningErrors.length > 0) {
                const currentAgents = warningErrors.map((w) => w.ai_agent).sort();
                const previousAgents = previousWarnings.value.map((w) => w.ai_agent).sort();

                if (arraysEqual(currentAgents, previousAgents)) {
                    clearErrors();
                    return true;
                } else {
                    bodyErrors.value = warningErrors;
                    previousWarnings.value = warningErrors;
                    return false;
                }
            }

            return true;
        } catch (err) {
            toasted.error(catchRequestError(err));
            return false;
        }
    };

    const clearErrors = () => {
        bodyErrors.value = [];
    };

    return {
        bodyErrors,
        previousWarnings,
        validate,
        clearErrors,
    };
};
